import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useMBList } from '../hooks/useMBList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Withdraw Group three-dot → Summary Modal.
 * Tabs: Today / Weekly / Monthly / Custom Date.
 */
const SummaryModal = ({ open, onClose, userId, assignedMbs, isAdmin }) => {
  const [range, setRange] = useState('today');
  const [date, setDate] = useState('');
  const [mb, setMb] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const liveMBs = useMBList();

  // Point 28 — a handler with an MB assignment only gets buttons for their
  // own MB(s); admins and unassigned handlers keep seeing every MB.
  const mbOptions = !isAdmin && assignedMbs && assignedMbs.length > 0
    ? ['all', ...assignedMbs]
    : ['all', ...liveMBs];

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (range === 'custom' && date) params.set('date', date);
      if (mb && mb !== 'all') params.set('mb', mb);
      if (userId) params.set('user_id', userId);
      const res = await axios.get(`${API}/withdraw-summary?${params.toString()}`);
      setData(res.data);
    } catch (e) { toast.error('Failed to load summary'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, range, date, mb]);

  if (!open) return null;
  const t = data?.totals || {};
  const fmt = (n) => `₹ ${(n || 0).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', maximumFractionDigits: 0 })}`;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden shadow-2xl" data-testid="summary-modal">
        <div className="p-4 border-b flex items-center justify-between bg-blue-600 text-white">
          <h3 className="text-lg font-bold flex items-center gap-2"><Funnel size={20} weight="bold" /> Summary</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-full"><X size={22} /></button>
        </div>

        <div className="p-3 border-b bg-gray-50 space-y-2">
          <div className="flex gap-1.5">
            {[
              { k: 'today',   l: 'Today' },
              { k: 'weekly',  l: 'Weekly' },
              { k: 'monthly', l: 'Monthly' },
              { k: 'custom',  l: 'Custom' },
            ].map(t => (
              <button
                key={t.k}
                onClick={() => setRange(t.k)}
                data-testid={`summary-tab-${t.k}`}
                className={`flex-1 py-1.5 rounded text-xs font-semibold border ${range === t.k ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}
              >{t.l}</button>
            ))}
          </div>
          {range === 'custom' && (
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-2 py-1 border rounded text-xs" data-testid="summary-date-input" />
          )}
          {/* MB filter — pick a specific MB or All. Handlers who work inside
              a single MB can now scope the summary to that MB directly. */}
          <div className="flex flex-wrap gap-1" data-testid="summary-mb-filter">
            {mbOptions.map(opt => (
              <button
                key={opt}
                onClick={() => setMb(opt)}
                data-testid={`summary-mb-${opt}`}
                className={`px-2 py-1 rounded text-[11px] font-semibold border transition-colors ${
                  mb === opt
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >{opt === 'all' ? 'All MB' : opt}</button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-amber-50 p-3 border border-amber-200">
                  <div className="text-[10px] uppercase font-bold text-amber-700">Pending Withdraw</div>
                  <div className="text-lg font-bold text-amber-900">{fmt(t.pending_amount)}</div>
                  <div className="text-[10px] text-amber-700">{t.pending_count || 0} requests</div>
                </div>
                <div className="rounded-lg bg-green-50 p-3 border border-green-200">
                  <div className="text-[10px] uppercase font-bold text-green-700">Paid Withdraw</div>
                  <div className="text-lg font-bold text-green-900">{fmt(t.paid_amount)}</div>
                  <div className="text-[10px] text-green-700">{t.paid_count || 0} requests</div>
                </div>
                <div className="rounded-lg bg-blue-50 p-3 border border-blue-200 col-span-2">
                  <div className="text-[10px] uppercase font-bold text-blue-700">Deposit Total</div>
                  <div className="text-lg font-bold text-blue-900">{fmt(t.deposit_amount)}</div>
                  <div className="text-[10px] text-blue-700">{t.deposit_count || 0} deposits</div>
                </div>
              </div>

              {(data?.breakdown || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-gray-500 mb-1.5 mt-2">Date-wise Breakdown</div>
                  <div className="space-y-1.5">
                    {data.breakdown.slice(0, 30).map(b => (
                      <div key={b.date} className="rounded border border-gray-200 p-2 text-xs bg-gray-50" data-testid={`summary-day-${b.date}`}>
                        <div className="font-semibold text-gray-700 mb-0.5">{b.date}</div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-600">
                          <span>Pending: <b className="text-amber-700">{fmt(b.pending_amount)}</b> ({b.pending_count})</span>
                          <span>Paid: <b className="text-green-700">{fmt(b.paid_amount)}</b> ({b.paid_count})</span>
                          {b.deposit_amount ? <span>Deposit: <b className="text-blue-700">{fmt(b.deposit_amount)}</b> ({b.deposit_count})</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryModal;
